<template>
  <div
    class="app"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
  >
    <div
      v-if="showLayout"
      class="app-container"
      :class="{
        'is-collapsed': isSidebarCollapsed,
        'is-mobile-open': isMobileSidebarOpen,
        'is-fullscreen-menu-open':
          isContentFullscreen && isMobileSidebarOpen,
        'is-content-fullscreen': isContentFullscreen,
      }"
    >
      <div
        class="mobile-overlay"
        v-if="isMobileSidebarOpen"
        @click="closeMobileSidebar"
      ></div>

      <AppSidebar
        class="sidebar"
        :collapsed="isSidebarCollapsed"
        :mobile-open="isMobileSidebarOpen"
        @toggle-collapse="toggleSidebar"
        @close-mobile="closeMobileSidebar"
      />

      <main class="app-main">
        <AppHeader
          class="header"
          @toggle-mobile-menu="toggleMobileSidebar"
        />

        <div
          class="content"
          :class="{
            'content--with-floating-actions': showFloatingActions,
          }"
        >
          <router-view
            @content-fullscreen-change="handleContentFullscreenChange"
            @fullscreen-menu-open="openFullscreenMenu"
          />
        </div>
      </main>
    </div>

    <router-view v-else />

    <AppUpdateNotification />
    <AppFloatingActions
      v-if="showFloatingActions"
      :show-scroll-to-top="showLayout"
      :force-collapse-support-widget="forceCollapseSupportWidget"
      :above-form-footer="floatingActionsAboveFormFooter"
    />
    <base-button
      v-if="showDebugPageLink && !isContentFullscreen"
      class="debug-page-link"
      variant="ghost"
      icon="settings"
      icon-size="14"
      aria-label="Инструменты"
      @click="goToDebugPage"
    />
  </div>
</template>

<script>
import AppFloatingActions from "./components/AppFloatingActions.vue";
import AppHeader from "./components/AppHeader.vue";
import AppSidebar from "./components/AppSidebar.vue";
import AppUpdateNotification from "./components/AppUpdateNotification.vue";

import { useAuthStore } from "@/stores/authStore";
import { useUserDataStore } from "@/stores/userDataStore";
import { usePermissionsStore } from "@/stores/permissionsStore";
import { useChatStore } from "@/stores/chatStore";
import { isDebugFeatureEnabled } from "@/utils/debugFeatureUtils";
import { shouldShowFloatingActions } from "@/utils/appLayoutUtils";

import { mapState } from "pinia";

export default {
  name: "App",
  components: {
    AppFloatingActions,
    AppHeader,
    AppSidebar,
    AppUpdateNotification,
  },
  data() {
    return {
      isSidebarCollapsed: false,
      isMobileSidebarOpen: false,
      isContentFullscreen: false,

      touchStartX: 0,
      touchStartY: 0,
    };
  },
  computed: {
    ...mapState(useAuthStore, ["isAuthenticated"]),
    ...mapState(usePermissionsStore, ["isLoaded"]),
    ...mapState(useUserDataStore, ["currentCompany"]),

    permissionsStore() {
      return usePermissionsStore();
    },

    showLayout() {
      const noLayoutRoutes = [
        "Login",
        "AccountRecovery",
        "PasswordRecovery",
        "NotificationPreview",
        "PublicSharing",
        "NoCompanies",
      ];
      return (
        this.isAuthenticated &&
        !noLayoutRoutes.includes(this.$route.name) &&
        !(this.$route.name === "UserProfile" && !this.currentCompany)
      );
    },

    showFloatingActions() {
      return shouldShowFloatingActions({
        showLayout: this.showLayout,
        isAuthenticated: this.isAuthenticated,
        hasCompany: Boolean(this.currentCompany),
        routeName: this.$route.name,
        isContentFullscreen: this.isContentFullscreen,
      });
    },

    forceCollapseSupportWidget() {
      return Boolean(this.$route.meta.forceCollapseSupportWidget);
    },

    floatingActionsAboveFormFooter() {
      return Boolean(this.$route.meta.hasFixedFormFooter);
    },

    showDebugPageLink() {
      return (
        this.showLayout &&
        this.$route.path.startsWith("/settings") &&
        isDebugFeatureEnabled()
      );
    },

    chatUpdatesAllowed() {
      return (
        this.isAuthenticated &&
        this.permissionsStore.isLoaded &&
        this.permissionsStore.canSeePage("chats")
      );
    },
  },
  watch: {
    // Автоматически закрываем мобильное меню при переходе на другую страницу
    $route() {
      if (this.isMobileSidebarOpen) {
        this.closeMobileSidebar();
      }
    },

    isSidebarCollapsed(newValue) {
      localStorage.setItem("isSidebarCollapsed", newValue);
    },

    async isAuthenticated(newValue) {
      if (newValue) {
        const userDataStore = useUserDataStore();
        await userDataStore.loadUserData();

        const permissionsStore = usePermissionsStore();
        await permissionsStore.loadPermissions();
        permissionsStore.startPolling(this.$router);
      } else {
        usePermissionsStore().clearPermissions();
        useChatStore().resetState();
      }
    },

    chatUpdatesAllowed(newValue) {
      this.syncChatUpdates(newValue);
    },
  },

  methods: {
    handleContentFullscreenChange(fullscreen) {
      this.isContentFullscreen = fullscreen;
      if (fullscreen) this.closeMobileSidebar();
    },
    openFullscreenMenu() {
      this.isMobileSidebarOpen = true;
    },
    toggleSidebar() {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
    },
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },

    async syncChatUpdates(allowed = this.chatUpdatesAllowed) {
      const chatStore = useChatStore();

      if (!allowed) {
        chatStore.resetState();
        return;
      }

      await chatStore.fetchConversations();
      chatStore.setupConversationSubscription();
    },

    goToDebugPage() {
      if (this.$route.name === "Debug") return;

      this.$router.push({ name: "Debug" });
    },

    handleResize() {
      if (window.innerWidth <= 1024) {
        this.isSidebarCollapsed = false;
        this.isMobileSidebarOpen = false;
      }
    },

    handleTouchStart(e) {
      this.touchStartX = e.changedTouches[0].screenX;
      this.touchStartY = e.changedTouches[0].screenY;
    },

    handleTouchEnd(e) {
      const touchEndX = e.changedTouches[0].screenX;
      const touchEndY = e.changedTouches[0].screenY;

      const deltaX = touchEndX - this.touchStartX;
      const deltaY = touchEndY - this.touchStartY;

      // Если движение было больше вертикальным, чем горизонтальным - это скролл, игнорируем
      if (Math.abs(deltaY) > Math.abs(deltaX)) return;

      const MIN_SWIPE_DISTANCE = 50;
      const EDGE_ZONE_WIDTH = 40;

      if (
        !this.isMobileSidebarOpen &&
        this.touchStartX <= EDGE_ZONE_WIDTH &&
        deltaX > MIN_SWIPE_DISTANCE
      ) {
        this.isMobileSidebarOpen = true;
      }

      if (this.isMobileSidebarOpen && deltaX < -MIN_SWIPE_DISTANCE) {
        this.isMobileSidebarOpen = false;
      }
    },
  },

  async created() {
    const savedState = localStorage.getItem("isSidebarCollapsed");

    if (savedState !== null) {
      this.isSidebarCollapsed = savedState === "true";
    }

    if (this.isAuthenticated) {
      const userDataStore = useUserDataStore();
      await userDataStore.loadUserData();

      const permissionsStore = usePermissionsStore();
      await permissionsStore.loadPermissions();
      permissionsStore.startPolling(this.$router);

      await this.syncChatUpdates();
    }
  },

  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  unmounted() {
    window.removeEventListener("resize", this.handleResize);
    usePermissionsStore().stopPolling();
    useChatStore().disconnectSubscriptions();
  },
};
</script>

<style scoped>
.app {
  background-color: #f0f2f5;
}

.app-container {
  display: flex;
  min-height: 100dvh;
  position: relative;

  /* Ширина сайдбара по умолчанию */
  --sidebar-width: 220px;
  /* Ширина сайдбара в свернутом состоянии */
  --sidebar-width-collapsed: 70px;
  /* Высота хэдера */
  --header-height: 80px;
}

.app-container.is-collapsed {
  --sidebar-width: var(--sidebar-width-collapsed);
}

.app-container.is-content-fullscreen {
  --sidebar-width: 0px;
  --header-height: 0px;
}

.app-container.is-content-fullscreen .sidebar,
.app-container.is-content-fullscreen .header {
  display: none;
}

.app-container.is-content-fullscreen .content {
  --app-content-sticky-bottom-offset: 0;
  --app-content-sticky-inline-offset: 0;
  padding: 0;
}

.app-container.is-content-fullscreen.is-fullscreen-menu-open .sidebar {
  display: block;
  width: 250px;
  box-shadow: 4px 0 15px rgba(0, 0, 0, 0.3);
}

.app-container.is-content-fullscreen.is-fullscreen-menu-open.is-collapsed .sidebar {
  width: var(--sidebar-width-collapsed);
}

.app-container.is-content-fullscreen.is-fullscreen-menu-open .mobile-overlay {
  display: block;
}

.sidebar {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--sidebar-width);
  z-index: 2000;
  transition: width 0.3s ease, transform 0.3s ease;
}

.header {
  position: fixed;
  top: 0;
  left: var(--sidebar-width);
  right: 0;
  height: var(--header-height);
  z-index: 40;
  transition: left 0.3s ease, width 0.3s ease;
}

.app-main {
  position: fixed;
  top: var(--header-height);
  left: var(--sidebar-width);
  width: calc(100dvw - var(--sidebar-width));
  transition: left 0.3s ease, width 0.3s ease;
}

.debug-page-link {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 5000;
  width: 28px;
  min-width: 28px;
  height: 28px;
  min-height: 28px;
  padding: 0;
  border-color: transparent;
  background: rgba(255, 255, 255, 0.45);
  color: rgba(17, 24, 39, 0.38);
  opacity: 0.55;
}

.debug-page-link:hover,
.debug-page-link:focus-visible {
  background: rgba(255, 255, 255, 0.9);
  color: rgba(17, 24, 39, 0.78);
  opacity: 1;
}

.content {
  --app-content-sticky-bottom-offset: -30px;
  --app-content-sticky-inline-offset: -30px;
  position: relative;
  padding: 20px 30px 30px;
  height: calc(100dvh - var(--header-height));
  overflow-y: auto;
  scrollbar-gutter: stable;
  background-color: #f0f2f5;
}

.mobile-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1900;
  backdrop-filter: blur(2px);
  transition: opacity 0.3s ease;
}

@media (max-width: 1024px) {
  .app-container {
    --sidebar-width: 0px;
  }

  .sidebar {
    width: 250px !important;
    transform: translateX(-100%);
    box-shadow: none;
  }

  .app-container.is-mobile-open .sidebar {
    transform: translateX(0);
    box-shadow: 4px 0 15px rgba(0, 0, 0, 0.3);
  }

  .app-container.is-mobile-open .mobile-overlay {
    display: block;
  }

  .header {
    left: 0;
    width: 100%;
  }

  .app-main {
    left: 0;
    width: 100%;
  }
}

@media (max-width: 768px) {
  .content {
    --app-content-sticky-bottom-offset: -15px;
    --app-content-sticky-inline-offset: -15px;
    padding: 20px 15px 15px;
  }

  .content--with-floating-actions {
    padding-bottom: 136px;
  }
}
</style>
